import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_ALL_RESERVATION_EVENT } from '@/modules/API/daycare/eventDayHandler.ts'
import { EventDay, ReservationChild, ReservationEvent, ReservationParent } from '@/types/EventDay.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { TableContainer } from '@mui/material'
import styles from './presenceDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import Indicator from '@/components/_custom/Indicator/indicator.tsx'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { GET_EXPORT_DATA } from '@/modules/API/export/getExportData.ts'
import { downloadBase64File } from '@/modules/utils/downloader.ts'
import { ExportDataType } from '@/types/Export.ts'
import UserReservationEvent from './userReservationEvent/userReservationEvent.tsx'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import AddPresenceReservationDialog from './addPresence/addPresenceReservationDialog.tsx'
import { Daycare } from '@/types/Daycare.ts'

interface PresenceDialogProps {
    open: boolean
    handleClose: () => void
    event: EventDay
    daycare?: Daycare
    refetchAllEvents: () => Promise<void>
}

const PresenceDialog: FunctionComponent<PresenceDialogProps> = ({
    open,
    handleClose,
    event,
    daycare,
    refetchAllEvents
}: PresenceDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [reservations, setReservations] = useState<Array<ReservationEvent>>([])

    const [showReservationGlobal, setShowReservationGlobal] = useState<boolean>(false)

    const [showAddPresenceReservationDialog, setShowAddPresenceReservationDialog] = useState<boolean>(false)

    const [countChild, setCountChild] = useState<number>(0)
    const [countAdult, setCountAdult] = useState<number>(0)

    useEffect(() => {
        if (reservations.length > 0) {
            let countChild: number = 0
            let countAdult: number = 0
            reservations.forEach((reservation) => {
                countChild += reservation.reservationChild.length
                countAdult += reservation.reservationParent.length
            })
            setCountChild(countChild)
            setCountAdult(countAdult)
        }
    }, [reservations])

    const {
        loading: loadingReservation,
        data: dataReservation,
        refetch: refetchReservations
    } = useQuery(GET_ALL_RESERVATION_EVENT, {
        variables: {
            event_id: event.id,
            daycare_id: event.daycareId
        }
    })

    useEffect(() => {
        if (dataReservation) {
            setReservations(dataReservation.getAllReservationEvent)
        }
    }, [dataReservation])

    async function refetchAllReservations(): Promise<void> {
        const { data } = await refetchReservations()

        if (data.getAllReservationEvent) {
            setReservations(data.getAllReservationEvent)
        } else {
            setReservations([])
        }

        await refetchAllEvents()
    }

    const [getExportData, { data: dataExport, loading: loadingExport }] = useLazyQuery(GET_EXPORT_DATA, {
        variables: {
            daycareIds: [event.daycareId],
            type: ExportDataType.EventPresence,
            eventId: event.id
        }
    })

    useEffect(() => {
        if (dataExport && dataExport.getExportData) {
            downloadBase64File(
                dataExport.getExportData.base64Data,
                `${t('presence').trim().toLowerCase()}_${event.date}.xlsx`,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            )
        }
    }, [dataExport])

    const [numberOfUserOpen, setNumberOfUserOpen] = useState<number>(0)

    function openOrCloseUserReservation(open: boolean): void {
        const count: number = open ? numberOfUserOpen + 1 : numberOfUserOpen - 1

        if (count === reservations.length) {
            setShowReservationGlobal(true)
        } else if (count === 0) {
            setShowReservationGlobal(false)
        }
        setNumberOfUserOpen(count)
    }

    function handleShowReservation(checked: boolean) {
        setShowReservationGlobal(checked)
        setNumberOfUserOpen(checked ? reservations.length : 0)
    }

    const canUpdate: boolean = !!user?.currentDaycareRights.canWriteEvents

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('presence')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.TextContainer}>
                    <p className={styles.Title}>{event.title}</p>
                    <p className={styles.Date}>
                        {format(parse(event.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        }) +
                            ' ' +
                            t('from').toLowerCase() +
                            ' ' +
                            format(parse(event.startHour, 'HH:mm:ss', new Date()), 'HH:mm') +
                            ' ' +
                            t('to').toLowerCase() +
                            ' ' +
                            format(parse(event.endHour, 'HH:mm:ss', new Date()), 'HH:mm')}
                    </p>
                </div>
                {loadingReservation && <Loading overall={false} />}
                {loadingExport && <Loading overall={true} />}
                {dataReservation &&
                    (reservations.length === 0 ? (
                        <p> {t('noPresence')}</p>
                    ) : (
                        <div className={styles.Container}>
                            <div className={styles.IndicatorPlacement}>
                                {!event.parentOnly && (
                                    <Indicator count={countChild} text={t('childs')} className={styles.Indicator} />
                                )}
                                <Indicator count={countAdult} text={t('adults')} className={styles.Indicator} />
                            </div>
                            <TableContainer>
                                {[...reservations]
                                    .sort((a: ReservationEvent, b: ReservationEvent) =>
                                        a.nameUser.localeCompare(b.nameUser)
                                    )
                                    .map((reservation: ReservationEvent, indexResa: number) => (
                                        <UserReservationEvent
                                            daycareId={event.daycareId}
                                            eventId={event.id}
                                            reservation={reservation}
                                            showReservationGlobal={showReservationGlobal}
                                            key={indexResa + ' ' + showReservationGlobal}
                                            canUpdate={canUpdate}
                                            refetchAllReservations={refetchAllReservations}
                                            openOrCloseUserReservation={openOrCloseUserReservation}
                                        />
                                    ))}
                            </TableContainer>
                            <div
                                onClick={() => handleShowReservation(!showReservationGlobal)}
                                className={styles.LittleText}
                            >
                                {showReservationGlobal ? t('reduceAll') : t('showAll')}
                            </div>
                        </div>
                    ))}
                {user?.currentDaycareRights.canReadEvents && (
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'print'}
                            onClick={getExportData}
                            className={styles.Button}
                            disabled={loadingExport}
                        />
                        {canUpdate && (
                            <ButtonTextIcon
                                type={'add'}
                                onClick={() => setShowAddPresenceReservationDialog(true)}
                                className={styles.Button}
                                disabled={loadingExport}
                            />
                        )}
                    </div>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                </div>
            </DialogBody>
            {canUpdate && daycare && showAddPresenceReservationDialog && (
                <AddPresenceReservationDialog
                    open={showAddPresenceReservationDialog}
                    handleClose={() => setShowAddPresenceReservationDialog(false)}
                    daycare={daycare}
                    event={event}
                    refetchAllReservations={refetchAllReservations}
                    reservationChildIds={Array.from(
                        new Set(
                            reservations
                                .map((r: ReservationEvent): ReservationChild[] => r.reservationChild)
                                .flat()
                                .map((rc: ReservationChild) => rc.childId)
                        )
                    )}
                    reservationParents={reservations
                        .map((r: ReservationEvent): ReservationParent[] => r.reservationParent)
                        .flat()}
                    parentOnly={event.parentOnly}
                    reservations={reservations}
                />
            )}
        </Dialog>
    )
}

export default PresenceDialog
